"use client"

import { FC, useState } from "react"
import { useSpaceStatusTemplate } from "@/hooks/useSpaceStatusTemplate"
import { useSpacePriorityTemplate } from "@/hooks/useSpacePriorityTemplate"
import type {
  TaskFilterCondition,
  TaskFilterOperator
} from "@/lib/spaces/task-filters"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { Calendar } from "@/components/ui/calendar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  IconFilter,
  IconPlus,
  IconX,
  IconCalendar,
  IconUser,
  IconFlag,
  IconTag,
  IconClock,
  IconFolder,
  IconDeviceFloppy,
  IconTrash,
  IconRefresh,
  IconSearch
} from "@tabler/icons-react"
import { format } from "date-fns"
import { toast } from "sonner"

type FilterCondition = TaskFilterCondition

interface SavedFilter {
  id: string
  name: string
  description?: string
  conditions: FilterCondition[]
  is_default: boolean
  is_shared: boolean
  created_at: string
  created_by: string
}

interface AdvancedFilterProps {
  spaceId: string
  onFiltersChange?: (conditions: FilterCondition[]) => void
  savedFilters?: SavedFilter[]
  onSavedFiltersUpdate?: (filters: SavedFilter[]) => void
  customFields?: Array<{
    id: string
    name: string
    field_type: string
  }>
}

const fieldOptions = [
  { value: 'title', label: 'Title', type: 'text', icon: IconSearch },
  { value: 'status', label: 'Status', type: 'select', icon: IconFlag },
  { value: 'priority', label: 'Priority', type: 'select', icon: IconFlag },
  { value: 'assigned_to', label: 'Assignee', type: 'user', icon: IconUser },
  { value: 'due_date', label: 'Due Date', type: 'date', icon: IconCalendar },
  { value: 'created_at', label: 'Created Date', type: 'date', icon: IconCalendar },
  { value: 'updated_at', label: 'Updated Date', type: 'date', icon: IconCalendar },
  { value: 'time_estimate_minutes', label: 'Time Estimate', type: 'number', icon: IconClock },
  { value: 'time_spent_minutes', label: 'Time Spent', type: 'number', icon: IconClock },
  { value: 'folder_id', label: 'Folder', type: 'select', icon: IconFolder },
  { value: 'tags', label: 'Tags', type: 'tags', icon: IconTag }
]

const operators = {
  text: [
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Does not equal' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' }
  ],
  select: [
    { value: 'equals', label: 'Is' },
    { value: 'not_equals', label: 'Is not' },
    { value: 'in', label: 'Is any of' },
    { value: 'not_in', label: 'Is none of' }
  ],
  number: [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Does not equal' },
    { value: 'greater_than', label: 'Greater than' },
    { value: 'less_than', label: 'Less than' },
    { value: 'greater_equal', label: 'Greater than or equal' },
    { value: 'less_equal', label: 'Less than or equal' },
    { value: 'between', label: 'Between' },
    { value: 'is_empty', label: 'Is empty' }
  ],
  date: [
    { value: 'equals', label: 'Is' },
    { value: 'not_equals', label: 'Is not' },
    { value: 'greater_than', label: 'After' },
    { value: 'less_than', label: 'Before' },
    { value: 'date_range', label: 'Between' },
    { value: 'is_empty', label: 'Is empty' },
    { value: 'is_not_empty', label: 'Is not empty' }
  ],
  user: [
    { value: 'equals', label: 'Is' },
    { value: 'not_equals', label: 'Is not' },
    { value: 'in', label: 'Is any of' },
    { value: 'is_empty', label: 'Is unassigned' }
  ],
  tags: [
    { value: 'contains', label: 'Contains' },
    { value: 'not_contains', label: 'Does not contain' },
    { value: 'in', label: 'Contains any of' },
    { value: 'not_in', label: 'Contains none of' },
    { value: 'is_empty', label: 'Has no tags' }
  ]
}

export const AdvancedFilter: FC<AdvancedFilterProps> = ({
  spaceId,
  onFiltersChange,
  savedFilters = [],
  onSavedFiltersUpdate,
  customFields = []
}) => {
  const { availableStatuses } = useSpaceStatusTemplate(spaceId)
  const { availablePriorities } = useSpacePriorityTemplate(spaceId)
  const statusOptions = availableStatuses.length > 0 ? availableStatuses : ['todo', 'in_progress', 'done', 'blocked', 'cancelled']
  const priorityOptions = availablePriorities.length > 0 ? availablePriorities : ['low', 'medium', 'high', 'urgent']
  const [isOpen, setIsOpen] = useState(false)
  const [conditions, setConditions] = useState<FilterCondition[]>([])
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [filterName, setFilterName] = useState('')
  const [filterDescription, setFilterDescription] = useState('')
  const [isShared, setIsShared] = useState(false)

  // Add custom fields to field options
  const allFieldOptions = [
    ...fieldOptions,
    ...customFields.map(field => ({
      value: `custom_${field.id}`,
      label: field.name,
      type: field.field_type,
      icon: IconTag
    }))
  ]

  const addCondition = () => {
    const newCondition: FilterCondition = {
      id: `condition_${Date.now()}`,
      field: 'title',
      operator: 'contains',
      value: '',
      logic: conditions.length > 0 ? 'and' : undefined
    }
    setConditions([...conditions, newCondition])
  }

  const updateCondition = (id: string, updates: Partial<FilterCondition>) => {
    setConditions(conditions.map(condition => 
      condition.id === id ? { ...condition, ...updates } : condition
    ))
  }

  const removeCondition = (id: string) => {
    setConditions(conditions.filter(condition => condition.id !== id))
  }

  const clearFilters = () => {
    setConditions([])
    onFiltersChange?.([])
  }

  const applyFilters = () => {
    onFiltersChange?.(conditions)
    setIsOpen(false)
  }

  const saveFilter = async () => {
    if (!filterName.trim()) {
      toast.error('Filter name is required')
      return
    }

    if (conditions.length === 0) {
      toast.error('Add at least one filter condition')
      return
    }

    const newFilter: SavedFilter = {
      id: `filter_${Date.now()}`,
      name: filterName.trim(),
      description: filterDescription.trim() || undefined,
      conditions: conditions,
      is_default: false,
      is_shared: isShared,
      created_at: new Date().toISOString(),
      created_by: 'current_user'
    }

    onSavedFiltersUpdate?.([...savedFilters, newFilter])
    
    setIsSaveDialogOpen(false)
    setFilterName('')
    setFilterDescription('')
    setIsShared(false)
    
    toast.success('Filter saved successfully')
  }

  const loadSavedFilter = (filter: SavedFilter) => {
    setConditions(filter.conditions)
    onFiltersChange?.(filter.conditions)
    setIsOpen(false)
    toast.success(`Applied filter: ${filter.name}`)
  }

  const deleteSavedFilter = (filterId: string) => {
    if (!confirm('Are you sure you want to delete this saved filter?')) {
      return
    }

    onSavedFiltersUpdate?.(savedFilters.filter(f => f.id !== filterId))
    toast.success('Filter deleted')
  }

  const getFieldType = (fieldValue: string) => {
    const field = allFieldOptions.find(f => f.value === fieldValue)
    return field?.type || 'text'
  }

  const getDefaultOperator = (fieldValue: string): TaskFilterOperator => {
    const fieldType = getFieldType(fieldValue)
    return (operators[fieldType]?.[0]?.value || "contains") as TaskFilterOperator
  }

  const renderValueInput = (condition: FilterCondition) => {
    const fieldType = getFieldType(condition.field)
    
    // Skip value input for operators that don't need values
    if (['is_empty', 'is_not_empty'].includes(condition.operator)) {
      return null
    }

    switch (fieldType) {
      case 'select':
        if (condition.field === 'status') {
          return (
            <Select
              value={condition.value}
              onValueChange={(value) => updateCondition(condition.id, { value })}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                {statusOptions.map(status => (
                  <SelectItem key={status} value={status}>
                    {status.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        } else if (condition.field === 'priority') {
          return (
            <Select
              value={condition.value}
              onValueChange={(value) => updateCondition(condition.id, { value })}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Select priority" />
              </SelectTrigger>
              <SelectContent>
                {priorityOptions.map(priority => (
                  <SelectItem key={priority} value={priority}>
                    {priority}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )
        }
        break

      case 'date':
        if (condition.operator === 'date_range') {
          return (
            <div className="flex items-center gap-2">
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-32">
                    <IconCalendar size={16} className="mr-2" />
                    {condition.value?.start ? format(new Date(condition.value.start), 'MMM d') : 'From'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={condition.value?.start ? new Date(condition.value.start) : undefined}
                    onSelect={(date) => updateCondition(condition.id, {
                      value: { ...condition.value, start: date?.toISOString() }
                    })}
                  />
                </PopoverContent>
              </Popover>
              <span className="text-muted-foreground text-sm">to</span>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-32">
                    <IconCalendar size={16} className="mr-2" />
                    {condition.value?.end ? format(new Date(condition.value.end), 'MMM d') : 'To'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={condition.value?.end ? new Date(condition.value.end) : undefined}
                    onSelect={(date) => updateCondition(condition.id, {
                      value: { ...condition.value, end: date?.toISOString() }
                    })}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )
        } else {
          return (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-48">
                  <IconCalendar size={16} className="mr-2" />
                  {condition.value ? format(new Date(condition.value), 'MMM d, yyyy') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={condition.value ? new Date(condition.value) : undefined}
                  onSelect={(date) => updateCondition(condition.id, { value: date?.toISOString() })}
                />
              </PopoverContent>
            </Popover>
          )
        }

      case 'number':
        if (condition.operator === 'between') {
          return (
            <div className="flex items-center gap-2">
              <Input
                type="number"
                placeholder="Min"
                value={condition.value?.min || ''}
                onChange={(e) => updateCondition(condition.id, {
                  value: { ...condition.value, min: Number(e.target.value) }
                })}
                className="w-24"
              />
              <span className="text-muted-foreground text-sm">to</span>
              <Input
                type="number"
                placeholder="Max"
                value={condition.value?.max || ''}
                onChange={(e) => updateCondition(condition.id, {
                  value: { ...condition.value, max: Number(e.target.value) }
                })}
                className="w-24"
              />
            </div>
          )
        } else {
          return (
            <Input
              type="number"
              placeholder="Enter number"
              value={condition.value || ''}
              onChange={(e) => updateCondition(condition.id, { value: Number(e.target.value) })}
              className="w-48"
            />
          )
        }

      default:
        return (
          <Input
            placeholder="Enter value"
            value={condition.value || ''}
            onChange={(e) => updateCondition(condition.id, { value: e.target.value })}
            className="w-48"
          />
        )
    }
  }

  const activeFiltersCount = conditions.filter(c => c.value !== '' && c.value !== null && c.value !== undefined).length

  return (
    <div className="space-y-4">
      {/* Filter Button */}
      <div className="flex items-center gap-2">
        <Button
          variant={activeFiltersCount > 0 ? "default" : "outline"}
          onClick={() => setIsOpen(true)}
          className="relative"
        >
          <IconFilter size={16} className="mr-2" />
          Filter
          {activeFiltersCount > 0 && (
            <Badge className="ml-2 bg-white text-black">
              {activeFiltersCount}
            </Badge>
          )}
        </Button>

        {/* Quick Saved Filters */}
        {savedFilters.slice(0, 3).map(filter => (
          <Button
            key={filter.id}
            variant="outline"
            size="sm"
            onClick={() => loadSavedFilter(filter)}
          >
            {filter.name}
          </Button>
        ))}

        {activeFiltersCount > 0 && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <IconX size={14} className="mr-1" />
            Clear
          </Button>
        )}
      </div>

      {/* Active Filters Display */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {conditions.filter(c => c.value !== '' && c.value !== null && c.value !== undefined).map(condition => {
            const field = allFieldOptions.find(f => f.value === condition.field)
            const operator = operators[getFieldType(condition.field)]?.find(o => o.value === condition.operator)
            
            return (
              <Badge key={condition.id} variant="secondary" className="px-3 py-1">
                <span className="mr-2">{field?.label}</span>
                <span className="text-muted-foreground mr-2">{operator?.label}</span>
                <span className="mr-2">{String(condition.value)}</span>
                <IconX 
                  size={12} 
                  className="hover:text-destructive cursor-pointer"
                  onClick={() => {
                    removeCondition(condition.id)
                    onFiltersChange?.(conditions.filter(c => c.id !== condition.id))
                  }}
                />
              </Badge>
            )
          })}
        </div>
      )}

      {/* Filter Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-h-dvh overflow-y-auto p-4 sm:max-h-[85vh] sm:max-w-4xl sm:p-6">
          <DialogHeader>
            <DialogTitle>Advanced Filters</DialogTitle>
            <DialogDescription>
              Create complex filters to find exactly what you&apos;re looking for.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Saved Filters */}
            {savedFilters.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium">Saved Filters</h4>
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  {savedFilters.map(filter => (
                    <Card key={filter.id} className="hover:bg-muted/50 cursor-pointer">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1" onClick={() => loadSavedFilter(filter)}>
                            <div className="text-sm font-medium">{filter.name}</div>
                            <div className="text-muted-foreground text-xs">
                              {filter.conditions.length} condition{filter.conditions.length !== 1 ? 's' : ''}
                              {filter.is_shared && ' • Shared'}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => deleteSavedFilter(filter.id)}
                            className="text-muted-foreground hover:text-destructive size-8 p-0"
                          >
                            <IconTrash size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Filter Conditions */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Filter Conditions</h4>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={addCondition}>
                    <IconPlus size={14} className="mr-1" />
                    Add Condition
                  </Button>
                  {conditions.length > 0 && (
                    <Button variant="outline" size="sm" onClick={() => setIsSaveDialogOpen(true)}>
                      <IconDeviceFloppy size={14} className="mr-1" />
                      Save Filter
                    </Button>
                  )}
                </div>
              </div>

              {conditions.length === 0 ? (
                <div className="text-muted-foreground py-8 text-center">
                  <IconFilter size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No filter conditions yet. Add a condition to get started.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {conditions.map((condition, index) => (
                    <Card key={condition.id}>
                      <CardContent className="p-4">
                        <div className="flex flex-wrap items-center gap-3">
                          {/* Logic Operator */}
                          {index > 0 && (
                            <Select
                              value={condition.logic}
                              onValueChange={(value) => updateCondition(condition.id, { logic: value as 'and' | 'or' })}
                            >
                              <SelectTrigger className="w-20">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="and">AND</SelectItem>
                                <SelectItem value="or">OR</SelectItem>
                              </SelectContent>
                            </Select>
                          )}

                          {/* Field */}
                          <Select
                            value={condition.field}
                            onValueChange={(value) => updateCondition(condition.id, { 
                              field: value, 
                              operator: getDefaultOperator(value),
                              value: ''
                            })}
                          >
                            <SelectTrigger className="w-48">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {allFieldOptions.map(field => {
                                const Icon = field.icon
                                return (
                                  <SelectItem key={field.value} value={field.value}>
                                    <div className="flex items-center gap-2">
                                      <Icon size={14} />
                                      {field.label}
                                    </div>
                                  </SelectItem>
                                )
                              })}
                            </SelectContent>
                          </Select>

                          {/* Operator */}
                          <Select
                            value={condition.operator}
                            onValueChange={(value) => updateCondition(condition.id, { 
                              operator: value as TaskFilterOperator,
                              value: ''
                            })}
                          >
                            <SelectTrigger className="w-48">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {operators[getFieldType(condition.field)]?.map(op => (
                                <SelectItem key={op.value} value={op.value}>
                                  {op.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>

                          {/* Value */}
                          {renderValueInput(condition)}

                          {/* Remove Button */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeCondition(condition.id)}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <IconX size={14} />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button variant="outline" onClick={clearFilters}>
              <IconRefresh size={14} className="mr-2" />
              Clear All
            </Button>
            <Button onClick={applyFilters}>
              Apply Filters
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Filter Dialog */}
      <Dialog open={isSaveDialogOpen} onOpenChange={setIsSaveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save Filter</DialogTitle>
            <DialogDescription>
              Save this filter configuration for future use.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Filter Name</Label>
              <Input
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder="Enter filter name"
              />
            </div>

            <div>
              <Label>Description (optional)</Label>
              <Input
                value={filterDescription}
                onChange={(e) => setFilterDescription(e.target.value)}
                placeholder="Describe this filter"
              />
            </div>

            <div className="flex items-center justify-between">
              <div>
                <Label>Share with team</Label>
                <p className="text-muted-foreground text-xs">
                  Make this filter available to all team members
                </p>
              </div>
              <Switch checked={isShared} onCheckedChange={setIsShared} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsSaveDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveFilter}>
              Save Filter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
